import { useState, useCallback } from 'react';
import { apiService } from '../../../shared/services/api';
import { ApiRoutes } from '../../../shared/config/constants';
import { ClientInfo } from '../types';

interface UseClientSearchResult {
  searchByPhone: (phone: string) => Promise<ClientInfo | null>;
  isLoading: boolean;
  error: string | null;
  result: ClientInfo | null;
  clearResult: () => void;
}

export function useClientSearch(): UseClientSearchResult {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ClientInfo | null>(null);

  const searchByPhone = useCallback(async (phone: string): Promise<ClientInfo | null> => {
    setIsLoading(true);
    setError(null);
    setResult(null);

    try {
      // Normalize phone: remove all non-digits and format
      const normalizedPhone = phone.replace(/\D/g, '');

      // Validate format: should be 8800XXXXXXX (11 digits starting with 8800)
      if (!/^8800\d{7}$/.test(normalizedPhone)) {
        setError('Invalid phone format. Expected: 8-800-XXX-XX-XX');
        return null;
      }

      const response = await apiService.get<ClientInfo>(
        ApiRoutes.Clients.SearchByPhone,
        { phone: normalizedPhone }
      );

      if (response.success && response.data) {
        setResult(response.data);
        return response.data;
      } else {
        setError('Client not found');
        return null;
      }
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
            'Client not found';
      setError(errorMessage);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const clearResult = useCallback(() => {
    setResult(null);
    setError(null);
  }, []);

  return { searchByPhone, isLoading, error, result, clearResult };
}
